// Copyright 2026 TokenLat. Licensed under the Apache License, Version 2.0.
import { useEffect, useState } from 'react';

import {
  allowAuthorizationRecovery,
  beginAuthorization,
  finishAuthorization,
  safeReturnPath,
  tokenLatEnabled,
} from '@coze-foundation/account-adapter';

import { LoginPage } from '../routes/async-components';

export function TokenLatAuthPage() {
  const [enabled, setEnabled] = useState<boolean>();
  const [failed, setFailed] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const next = safeReturnPath(params.get('next') ?? params.get('redirect'));
  const reason = params.get('error');
  const loggedOut = params.has('logged_out');
  const zh = navigator.language.startsWith('zh');
  useEffect(() => {
    document.title = zh ? '智能体工坊' : 'LatCraft';
  }, [zh]);
  useEffect(() => {
    // This standalone route does not mount the editor's responsive layout.
    // Remove its desktop minimum size only while the authorization page is open.
    const previous = [document.documentElement, document.body].map(element => ({
      element,
      minWidth: element.style.minWidth,
      minHeight: element.style.minHeight,
    }));
    previous.forEach(({ element }) => {
      element.style.minWidth = '0';
      element.style.minHeight = '0';
    });
    return () =>
      previous.forEach(({ element, minWidth, minHeight }) => {
        element.style.minWidth = minWidth;
        element.style.minHeight = minHeight;
      });
  }, []);

  useEffect(() => {
    let active = true;
    tokenLatEnabled()
      .then(value => {
        if (!active) {
          return;
        }
        setEnabled(value);
        if (value && !reason && !loggedOut && allowAuthorizationRecovery()) {
          beginAuthorization(next, 'login');
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [next, reason, loggedOut]);

  if (enabled === false) {
    return <LoginPage />;
  }
  const status = failed ? 'unavailable' : loggedOut ? 'logout' : reason;
  const messages: Record<string, [string, string]> = {
    unavailable: [
      '登录服务暂时不可用，请稍后重试。',
      'Sign-in is temporarily unavailable. Please retry later.',
    ],
    denied: [
      '授权已取消或账号无权访问。',
      'Authorization was cancelled or access was denied.',
    ],
    failed: [
      '授权未完成，请重新发起登录。',
      'Authorization could not be completed. Please sign in again.',
    ],
    logout: ['已退出智能体工坊。', 'You have signed out of LatCraft.'],
  };
  const copy = messages[status ?? ''] ?? [
    '使用 TokenLat 账号授权登录智能体工坊。',
    'Authorize with your TokenLat account to enter LatCraft.',
  ];
  const message = copy[zh ? 0 : 1];
  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: '#f7f8fa',
        color: '#171a24',
      }}
    >
      <section
        style={{
          width: 'min(420px, 90vw)',
          padding: 32,
          borderRadius: 16,
          background: '#fff',
          boxShadow: '0 8px 40px #0000000d',
        }}
      >
        <img src="/favicon.png" alt="TokenLat" width={48} height={48} />
        <h1 style={{ fontSize: 26 }}>
          {zh ? 'LatCraft · 智能体工坊' : 'LatCraft'}
        </h1>
        <p role="status" style={{ margin: '12px 0 24px', lineHeight: 1.7 }}>
          {enabled === undefined && !failed
            ? zh
              ? '正在连接登录服务…'
              : 'Connecting…'
            : message}
        </p>
        {enabled === true ? (
          <button
            type="button"
            onClick={() => beginAuthorization(next, 'login')}
            style={{
              padding: '12px 20px',
              border: 0,
              borderRadius: 8,
              background: '#5147eb',
              color: '#fff',
              cursor: 'pointer',
              width: '100%',
            }}
          >
            {zh ? '使用 TokenLat 授权登录' : 'Sign in with TokenLat'}
          </button>
        ) : null}
        {failed || reason === 'unavailable' ? (
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              display: 'block',
              marginTop: 16,
              cursor: 'pointer',
              color: '#5147eb',
            }}
          >
            {zh ? '重试' : 'Retry'}
          </button>
        ) : null}
      </section>
    </main>
  );
}

export function TokenLatAuthComplete() {
  useEffect(() => {
    finishAuthorization();
    const next = safeReturnPath(
      new URLSearchParams(window.location.search).get('next'),
    );
    window.location.replace(next);
  }, []);
  return <p role="status">LatCraft · 智能体工坊…</p>;
}
